import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Package, Award, User, Pencil, Check, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileEditor = ({ user }) => {
  const { checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm({ name: user?.name || '', phone: user?.phone || '', address: user?.address || '' });
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/auth/me`, form, { withCredentials: true });
      toast.success('Profil mis à jour');
      setEditing(false);
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!editing) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Nom</label>
          <p className="text-foreground font-medium">{user?.name}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
          <p className="text-foreground font-medium">{user?.email}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Téléphone</label>
          <p className={`font-medium ${user?.phone ? 'text-foreground' : 'text-destructive'}`}>
            {user?.phone || 'Non renseigné'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Adresse</label>
          <p className={`font-medium ${user?.address ? 'text-foreground' : 'text-destructive'}`}>
            {user?.address || 'Non renseignée'}
          </p>
        </div>
        {(!user?.phone || !user?.address) && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-3 text-sm text-yellow-900">
            Complétez votre téléphone et votre adresse pour accélérer vos prochaines commandes.
          </div>
        )}
        <button
          onClick={() => setEditing(true)}
          className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2"
          data-testid="edit-profile-button"
        >
          <Pencil className="w-4 h-4" />
          Modifier mon profil
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Nom *</label>
        <input
          type="text" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
          data-testid="profile-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Téléphone</label>
        <input
          type="tel" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="06 12 34 56 78"
          className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
          data-testid="profile-phone-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Adresse de livraison</label>
        <textarea
          value={form.address} rows="3"
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Numéro, rue, code postal, ville"
          className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
          data-testid="profile-address-input"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit" disabled={loading}
          className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2 disabled:opacity-50"
          data-testid="save-profile-button"
        >
          <Check className="w-4 h-4" />
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button
          type="button" onClick={() => setEditing(false)}
          className="btn-secondary px-4 py-2 rounded-sm font-medium"
        >
          Annuler
        </button>
      </div>
    </form>
  );
};

const Account = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    if (user && user !== false) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-success';
      case 'pending':
        return 'text-secondary-foreground bg-secondary';
      case 'shipped':
        return 'text-primary';
      case 'delivered':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8" style={{ fontFamily: 'IBM Plex Sans' }}>
          Mon compte
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-sm border border-border p-4 space-y-2">
              <button
                onClick={() => setActiveTab('orders')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-left ${
                  activeTab === 'orders'
                    ? 'bg-brand-orange text-white'
                    : 'hover:bg-surface'
                }`}
                data-testid="orders-tab-button"
              >
                <Package className="w-5 h-5" />
                <span>Mes commandes</span>
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-left ${
                  activeTab === 'profile'
                    ? 'bg-brand-orange text-white'
                    : 'hover:bg-surface'
                }`}
                data-testid="profile-tab-button"
              >
                <User className="w-5 h-5" />
                <span>Profil</span>
              </button>
              <button
                onClick={() => setActiveTab('loyalty')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-left ${
                  activeTab === 'loyalty'
                    ? 'bg-brand-orange text-white'
                    : 'hover:bg-surface'
                }`}
                data-testid="loyalty-tab-button"
              >
                <Award className="w-5 h-5" />
                <span>Fidélité</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">

            {activeTab === 'orders' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Mes commandes
                </h2>
                {orders.length === 0 ? (
                  <div className="bg-white rounded-sm border border-border p-8 text-center">
                    <p className="text-muted-foreground mb-4">Vous n'avez pas encore de commande</p>
                    <Link
                      to="/products"
                      className="inline-block btn-primary px-6 py-3 rounded-sm font-semibold"
                    >
                      Découvrir nos produits
                    </Link>
                  </div>
                ) : (
                  orders.map((order) => (
                    <div
                      key={order.id}
                      className="bg-white rounded-sm border border-border p-6"
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold">
                            Commande #{order.id.substring(0, 8)}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-sm text-sm font-medium ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {order.status === 'confirmed'
                            ? 'Confirmée'
                            : order.status === 'pending'
                            ? 'En attente'
                            : order.status === 'shipped'
                            ? 'Expédiée'
                            : order.status === 'delivered'
                            ? 'Livrée'
                            : order.status}
                        </span>
                      </div>
                      <div className="space-y-2 mb-4">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>
                              {item.product_name} x{item.quantity}
                            </span>
                            <span className="font-medium">{item.subtotal.toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                      {order.shipping && (
                        <div className="mb-4 bg-surface border border-border rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`order-tracking-${order.id}`}>
                          <Truck className="w-5 h-5 text-brand-orange flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-medium">Expédié via {order.shipping.carrier}</p>
                            <p className="text-muted-foreground">N° de suivi : <span className="font-mono text-foreground">{order.shipping.tracking_number}</span></p>
                          </div>
                          {order.shipping.tracking_url && (
                            <a href={order.shipping.tracking_url} target="_blank" rel="noreferrer"
                              className="ml-auto btn-primary px-4 py-2 rounded-sm text-sm font-semibold"
                              data-testid={`track-order-${order.id}`}>
                              Suivre mon colis
                            </a>
                          )}
                        </div>
                      )}
                      <div className="border-t border-border pt-4 flex justify-between items-center">
                        <span className="font-bold">Total</span>
                        <span className="text-xl font-bold text-primary">
                          {order.total.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="bg-white rounded-sm border border-border p-6">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Profil
                </h2>
                <ProfileEditor user={user} />
              </div>
            )}

            {activeTab === 'loyalty' && (
              <div className="bg-white rounded-sm border border-border p-6">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Programme de fidélité
                </h2>
                <div className="text-center py-8">
                  <Award className="w-16 h-16 text-secondary mx-auto mb-4" />
                  <div className="text-5xl font-bold text-primary mb-2">
                    {user?.loyalty_points || 0}
                  </div>
                  <p className="text-muted-foreground mb-6">Points de fidélité</p>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Gagnez 1 point pour chaque euro dépensé. Utilisez vos points pour obtenir des
                    réductions sur vos prochains achats !
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;